'use client';

import { clsx } from 'clsx';
import { motion, AnimatePresence } from 'motion/react';
import React, { forwardRef, useEffect, useState } from 'react';

export interface CheckboxProps {
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
  label?: string | React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'primary' | 'success' | 'warning' | 'error';
  textVariant?: 'default' | 'primary' | 'success' | 'warning' | 'error' | 'gray';
  className?: string;
  labelClassName?: string;
  id?: string;
  name?: string;
  value?: string;
  type?: 'checkbox' | 'radio';
}

const variantClasses = {
  default: {
    checked: 'bg-gray-900 border-gray-900',
    unchecked: 'bg-white border-gray-300',
    hover: 'hover:border-gray-400',
    focus: 'focus:ring-gray-500',
  },
  primary: {
    checked: 'bg-blue-600 border-blue-600',
    unchecked: 'bg-white border-gray-300',
    hover: 'hover:border-blue-400',
    focus: 'focus:ring-blue-500',
  },
  success: {
    checked: 'bg-green-600 border-green-600',
    unchecked: 'bg-white border-gray-300',
    hover: 'hover:border-green-400',
    focus: 'focus:ring-green-500',
  },
  warning: {
    checked: 'bg-yellow-500 border-yellow-500',
    unchecked: 'bg-white border-gray-300',
    hover: 'hover:border-yellow-400',
    focus: 'focus:ring-yellow-500',
  },
  error: {
    checked: 'bg-red-600 border-red-600',
    unchecked: 'bg-white border-gray-300',
    hover: 'hover:border-red-400',
    focus: 'focus:ring-red-500',
  },
};

const textVariantClasses = {
  default: {
    checked: 'text-gray-900',
    unchecked: 'text-gray-700',
  },
  primary: {
    checked: 'text-blue-600',
    unchecked: 'text-gray-700',
  },
  success: {
    checked: 'text-green-600',
    unchecked: 'text-gray-700',
  },
  warning: {
    checked: 'text-yellow-500',
    unchecked: 'text-gray-700',
  },
  error: {
    checked: 'text-red-600',
    unchecked: 'text-gray-700',
  },
  gray: {
    checked: 'text-gray-900',
    unchecked: 'text-gray-500',
  },
};

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      checked = false,
      onChange,
      disabled = false,
      label,
      size = 'md',
      variant = 'default',
      textVariant = 'default',
      className,
      labelClassName,
      id,
      name,
      value,
      type = 'checkbox',
      ...props
    },
    ref
  ) => {
    const [cid, setCid] = useState<string>(id || '');
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!disabled && onChange) {
        onChange(e.target.checked);
      }
    };

    const sizeClasses = {
      sm: 'w-4 h-4',
      md: 'w-5 h-5',
      lg: 'w-6 h-6',
    };

    useEffect(() => {
      if (cid) return;

      setCid('checkbox-' + Math.random().toString(36).substring(2, 15));
    }, [cid]);

    const checkmarkSizes = {
      sm: { width: type === 'radio' ? 8 : 12, height: type === 'radio' ? 8 : 12 },
      md: { width: type === 'radio' ? 10 : 16, height: type === 'radio' ? 10 : 16 },
      lg: { width: type === 'radio' ? 12 : 20, height: type === 'radio' ? 12 : 20 },
    };

    return (
      <div className="flex items-center space-x-2">
        <div className="relative">
          <input
            ref={ref}
            type="checkbox"
            id={cid}
            name={name}
            value={value}
            checked={checked}
            onChange={handleChange}
            disabled={disabled}
            className="sr-only"
            {...props}
          />
          {/* Container để tránh layout shift */}
          <div className={clsx('relative', sizeClasses[size])}>
            <motion.label
              htmlFor={cid}
              className={clsx(
                'absolute inset-0 inline-flex items-center justify-center rounded border-2 cursor-pointer transition-colors duration-200',
                type === 'radio' ? 'rounded-full' : 'rounded-sm',
                checked ? variantClasses[variant].checked : variantClasses[variant].unchecked,
                !disabled && variantClasses[variant].hover,
                !disabled &&
                  `focus-within:ring-2 focus-within:ring-offset-2 ${variantClasses[variant].focus}`,
                disabled && 'cursor-not-allowed',
                className
              )}
              whileHover={!disabled ? { scale: 1.05 } : {}}
              whileTap={!disabled ? { scale: 0.95 } : {}}
              initial={false}
              animate={{
                scale: checked ? 1.05 : 1,
              }}
              transition={{
                scale: { duration: 0.2, ease: 'easeOut' },
              }}
            >
              <AnimatePresence>
                {checked && (
                  <motion.svg
                    className="text-white"
                    width={checkmarkSizes[size].width}
                    height={checkmarkSizes[size].height}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ pathLength: 1, opacity: 1 }}
                    exit={{ pathLength: 0, opacity: 0 }}
                    transition={{
                      pathLength: { duration: 0.3, ease: 'easeInOut' },
                      opacity: { duration: 0.2 },
                    }}
                  >
                    <motion.path
                      d="M20 6L9 17l-5-5"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                    />
                  </motion.svg>
                )}
              </AnimatePresence>

              {/* Ripple effect */}
              <motion.div
                className={clsx(
                  'absolute inset-0 rounded border-2 border-current opacity-0',
                  type === 'radio' ? 'rounded-full' : 'rounded-sm'
                )}
                initial={false}
                animate={{
                  scale: checked ? [1, 1.5, 1] : 1,
                  opacity: checked ? [0, 0.3, 0] : 0,
                }}
                transition={{
                  duration: 0.6,
                  ease: 'easeOut',
                }}
              />
            </motion.label>
          </div>
        </div>

        {label && (
          <motion.label
            htmlFor={cid}
            className={clsx(
              'font-medium cursor-pointer select-none',
              disabled && 'opacity-50 cursor-not-allowed',
              checked
                ? textVariantClasses[textVariant].checked
                : textVariantClasses[textVariant].unchecked,
              labelClassName
            )}
            transition={{ duration: 0.2 }}
          >
            {label}
          </motion.label>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';

export default Checkbox;
